/* eslint-disable max-lines-per-function */
/* eslint-disable no-irregular-whitespace */
import { Button, classNames, Spacing, Text, Title } from '@vkontakte/vkui';
import { xray } from 'lib/xray';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PROMO_TARIFFS } from 'reactApp/appHelpers/configHelpers';
import { overquotaUserLockInPublic } from 'reactApp/appHelpers/featuresHelpers/features/publicOverquota';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { loadUserQuotaRequest } from 'reactApp/modules/userQuota/userQuota.module';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './Overquota.css';

const getDiscriptionText = (overquotaSize: string, isMobile?: boolean) => {
    switch (overquotaUserLockInPublic?.variant) {
        case 'c':
            return isMobile
                ? 'Ваше Облако переполнено — увеличьте место с подпиской Mail Space. 256 ГБ ваши бесплатно на 30 дней'
                : 'Загрузить файлы в ваше Облако тоже не получится — оно переполнено. Увеличьте место с подпиской Mail Space. 256 ГБ ваши бесплатно на 30 дней';
        case 'd':
            return isMobile
                ? 'Ваше Облако переполнено — увеличьте место с подпиской Mail Space. 256 ГБ ваши со скидкой 50%'
                : 'Загрузить файлы в ваше Облако тоже не получится — оно переполнено. Увеличьте место с подпиской Mail Space. 256 ГБ ваши со скидкой 50%';
        case 'b':
        default:
            return isMobile
                ? 'Загрузить файлы в ваше Облако тоже не получится — оно переполнено. Увеличьте место с подпиской Mail Space'
                : `Загрузить файлы в ваше Облако тоже не получится — оно переполнено на <strong data-test-id="overquota-size">${overquotaSize}</strong>. Увеличьте место с подпиской Mail Space, чтобы снять все ограничения`;
    }
};

const getButtonText = () => {
    switch (overquotaUserLockInPublic?.variant) {
        case 'c':
            return 'Снять ограничения бесплатно';
        case 'd':
            return 'Снять ограничения со скидкой';
        case 'b':
        default:
            return 'Снять ограничения';
    }
};

const getTariff = () => {
    switch (overquotaUserLockInPublic?.variant) {
        case 'c':
            return PROMO_TARIFFS.publicOverquota256GB1mounth;
        case 'd':
            return PROMO_TARIFFS.publicOverquota256GB1year;
        case 'b':
        default:
            return PROMO_TARIFFS?.heaviFile256GB;
    }
};

const title = 'Смотреть и скачивать файлы пока нельзя';

export const Overquota = memo(function Overquota({
    overquotaSize,
    isMobile,
    isFolder,
}: {
    overquotaSize: string;
    isMobile?: boolean;
    isFolder?: boolean;
}): ReactElement {
    const dispatch = useDispatch();
    const isDarkMode = isDarkThemeModeEnabled();

    const updateUserQuota = useCallback(() => {
        dispatch(loadUserQuotaRequest());
    }, [dispatch]);

    const imageSrc = useMemo(() => {
        if (isFolder) {
            return `${cdnPackagesUrl}/static/cloud/overquota/public/folder/${
                isDarkMode ? (isMobile ? 'dark' : 'viewer') : isMobile ? 'white' : 'light'
            }.png`;
        }

        return `${cdnPackagesUrl}/static/cloud/overquota/public/file/${isMobile ? 'dark' : 'viewer'}.png`;
    }, [isFolder, isMobile, isDarkMode]);

    const dwhName = useMemo(() => {
        return `mnz_overquota_public_${isFolder ? 'folder' : 'file'}_block${
            overquotaUserLockInPublic?.variant === 'c' ? '_trial' : overquotaUserLockInPublic?.variant === 'd' ? '_50discount' : ''
        }`;
    }, [isFolder]);

    const normalRadarName = useMemo(() => {
        return `${isMobile ? 'touch' : 'web'}-public-${isFolder ? 'folder' : 'file'}-block`;
    }, [isFolder, isMobile]);

    const onPrimaryHandler = useCallback(() => {
        const variant = ['b', 'c', 'd'].includes(overquotaUserLockInPublic?.variant) ? overquotaUserLockInPublic?.variant : 'b';
        const title =
            variant === 'b'
                ? 'Подписка Mail Space: 256 ГБ на месяц'
                : variant === 'c'
                ? 'Подписка Mail Space на месяц бнсплатно'
                : 'Подписка Mail Space на год со скидкой';

        sendDwh({
            eventCategory: ECategoryGa.splashscreen_alert,
            action: 'click',
            dwhData: {
                name: dwhName,
                platform: isMobile ? 'touch' : 'web',
            },
        });

        xray.send(normalRadarName, { i: 'click' });

        dispatch(
            loadProductsAndOpenFastCheckout({
                productId: getTariff(),
                paySource: dwhName,
                title,
                hideOtherTariffs: true,
                onSuccess: updateUserQuota,
            })
        );
    }, [dispatch, dwhName, isMobile, normalRadarName, updateUserQuota]);

    useEffect(() => {
        sendDwh({
            eventCategory: ECategoryGa.splashscreen_alert,
            action: 'show',
            dwhData: {
                name: dwhName,
                platform: isMobile ? 'touch' : 'web',
            },
        });

        xray.send(normalRadarName, { i: 'show' });
    }, []);

    return (
        <div className={styles.root} data-test-id="overquota-block">
            <div className={classNames(styles.wrapper)}>
                <img
                    src={imageSrc}
                    className={classNames(styles.img, {
                        [styles.mobileImg]: isMobile,
                        [styles.imgFolder]: isFolder,
                        [styles.mobileImgFolder]: isMobile && isFolder,
                    })}
                />
                <div className={classNames({ [styles.mobilewrapper]: isMobile })}>
                    <Title size={isMobile ? 2 : 1} className={classNames({ [styles.singlePublicFileText]: !isFolder })}>
                        {title}
                    </Title>
                    <Spacing size={isMobile ? 8 : 12} />
                    <Text className={classNames({ [styles.singlePublicFileText]: !isFolder })}>
                        <span dangerouslySetInnerHTML={{ __html: getDiscriptionText(overquotaSize, isMobile) }} />
                    </Text>
                    <Spacing size={isMobile ? 24 : 32} />
                    <Button
                        mode="primary"
                        size="l"
                        onClick={onPrimaryHandler}
                        className={styles.button}
                        data-test-id="button"
                        stretched={isMobile}
                    >
                        {getButtonText()}
                    </Button>
                </div>
            </div>
        </div>
    );
});
